import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiCheck, FiMapPin, FiPhone } from "react-icons/fi";
import { MdLogin } from "react-icons/md";
import logo from "../assets/logo.png";
import qrPreview from "../assets/qr-preview.jpeg";
import VisitorCounter from "../components/VisitorCounter";
import { HOME_DESCRIPTION, HOME_TITLE, applySeo } from "../seo";
import { getHomepageContent } from "../utils/api";
import {
  DEFAULT_HOMEPAGE,
  mergeHomepage,
  phoneNumbers,
  telHref,
  whatsappHref,
  whatsappNumbers,
} from "../utils/homepageContent";
import useStore from "../utils/store";

const Home = () => {
  const user = useStore((state) => state.user);
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [content, setContent] = useState(DEFAULT_HOMEPAGE);

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (content.faqs?.items || []).map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    }),
    [content]
  );

  useEffect(() => {
    applySeo({
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      path: "/",
      index: true,
      jsonLd: faqJsonLd,
    });
  }, [faqJsonLd]);

  useEffect(() => {
    getHomepageContent().then(([ok, data]) => {
      if (ok && data?.content) {
        setContent(mergeHomepage(DEFAULT_HOMEPAGE, data.content));
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.type === "Admin" || user.type === "ViewAdmin") {
      navigate("/admin");
    }
    if (user.type === "Customer") {
      if (user?.customer?.qrCodeEnabled) {
        navigate("/qr-portal");
      } else {
        navigate("/client");
      }
    }
    if (user.type === "MarketingStaff") {
      navigate("/staff/marketing");
    }
    if (user.type === "TradingStaff") {
      navigate("/staff/trading");
    }
    if (user.type === "TagStaff") {
      navigate("/staff/tag");
    }
    if (user.type === "StancilStaff") {
      navigate("/staff/stancil");
    }
    if (user.type === "OtherStaff") {
      navigate("/staff/other");
    }
    if (user.type === "Dispatch") {
      navigate("/staff/dispatch");
    }
  }, [user, navigate]);

  if (user) return null;

  const header = content.header || {};
  const hero = content.hero || {};
  const agencies = content.agencies || {};
  const how = content.howItWorks || {};
  const features = content.features || {};
  const printers = content.printers || {};
  const audience = content.audience || {};
  const faqs = content.faqs || {};
  const contact = content.contact || {};
  const footer = content.footer || {};

  return (
    <div className="riceqr-login min-h-[100dvh] bg-[#030617] text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#030617]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="RiceQRCodes"
              width={931}
              height={215}
              decoding="async"
              className="h-10 w-auto max-w-[240px] object-contain object-left md:h-11 md:max-w-[280px]"
            />
            <span className="hidden rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold tracking-[0.12em] text-emerald-400 sm:inline-flex">
              {header.kmsBadge}
            </span>
          </div>
          <nav aria-label="Page" className="hidden items-center gap-5 text-xs font-semibold text-slate-300 md:flex">
            <a href="#how-it-works" className="inline-flex min-h-12 items-center hover:text-white">
              {header.navHow}
            </a>
            <a href="#features" className="inline-flex min-h-12 items-center hover:text-white">
              {header.navFeatures}
            </a>
            <a href="#contact" className="inline-flex min-h-12 items-center hover:text-white">
              {header.navContact}
            </a>
          </nav>
          <Link
            to="/login"
            className="riceqr-login-btn-miller inline-flex min-h-12 items-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-slate-900"
          >
            {header.loginButton}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </header>

      <main>
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 md:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:py-16">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-xs font-bold tracking-[0.14em] text-emerald-400">
            {hero.eyebrow}
          </div>
          <h1 className="mt-5 text-[2.4rem] font-extrabold leading-[1.12] tracking-tight md:text-[3.35rem]">
            {hero.titleBefore}{" "}
            <span className="text-[#2EE6A0]">{hero.titleHighlight1}</span>{" "}
            {hero.titleMid}{" "}
            <span className="text-[#2EE6A0]">{hero.titleHighlight2}</span>{" "}
            {hero.titleAfter}
          </h1>
          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-slate-400">
            {hero.subtitle}
          </p>
          <div className="mt-7">
            <Link
              to="/login"
              className="riceqr-login-btn-miller inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-slate-900"
            >
              <MdLogin size={18} aria-hidden="true" />
              {hero.loginButton}
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <FiCheck className="text-emerald-400" aria-hidden="true" /> {hero.check1}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FiCheck className="text-emerald-400" aria-hidden="true" /> {hero.check2}
            </span>
          </div>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-0 rounded-[2rem] bg-emerald-400/10 blur-3xl" />
          <div className="relative rounded-[1.6rem] border border-emerald-400/20 bg-[#0b1224] p-4 shadow-[0_0_80px_rgba(46,230,160,0.12)]">
            <div className="mb-3 flex items-center justify-between text-xs font-semibold text-slate-300">
              <span>{hero.previewLabel}</span>
              <span className="rounded-md border border-white/10 px-2 py-0.5 text-emerald-300">
                {hero.previewBadge}
              </span>
            </div>
            <div className="aspect-[4/5] overflow-hidden rounded-xl bg-white">
              <img
                src={qrPreview}
                alt="Government rice QR tag preview showing a scannable gunny bag label"
                width={1184}
                height={1457}
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-contain"
              />
            </div>
            <p className="mt-3 text-center text-xs text-slate-300">
              {hero.previewCaption}
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#050b1c] py-10">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <p className="mb-6 text-center text-xs font-bold tracking-[0.16em] text-slate-300">
            {agencies.heading}
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {(agencies.items || []).map((agency) => (
              <div
                key={agency.name}
                className="rounded-xl border border-white/10 bg-[#0b1224] px-3 py-4 text-center"
              >
                <p className="text-sm font-extrabold tracking-wide">{agency.name}</p>
                <p className="mt-1 text-xs text-slate-300">{agency.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-14 md:px-8">
        <p className="text-center text-xs font-bold tracking-[0.14em] text-emerald-400">
          {how.eyebrow}
        </p>
        <h2 className="mt-3 text-center text-3xl font-extrabold md:text-4xl">
          {how.title}
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(how.steps || []).map((step) => (
            <div
              key={step.num + step.title}
              className="rounded-xl border border-white/10 bg-[#0b1224] p-5"
            >
              <p className="text-xs font-black tracking-[0.2em] text-amber-400">
                {step.num}
              </p>
              <h3 className="mt-3 text-lg font-extrabold">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {step.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section id="features" className="scroll-mt-24 border-y border-white/5 bg-[#050b1c] py-14">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <p className="text-center text-xs font-bold tracking-[0.14em] text-emerald-400">
            {features.eyebrow}
          </p>
          <h2 className="mt-3 text-center text-3xl font-extrabold md:text-4xl">
            {features.title}
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(features.items || []).map((feature) => (
              <div
                key={feature.title}
                className="rounded-xl border border-white/10 bg-[#0b1224] p-5"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                  <FiCheck size={14} strokeWidth={3} aria-hidden="true" />
                </span>
                <h3 className="mt-3 text-base font-extrabold">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {feature.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 md:px-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] text-emerald-400">
            {printers.eyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-extrabold leading-tight md:text-4xl">
            {printers.title}
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
            {printers.text}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(printers.items || []).map((printer) => (
            <div
              key={printer.name}
              className="rounded-xl border border-white/10 bg-[#0b1224] px-4 py-4"
            >
              <p className="text-base font-extrabold">{printer.name}</p>
              <p className="mt-1 text-xs text-emerald-400">{printer.models}</p>
              <p className="mt-1 text-xs text-slate-300">
                {printers.itemNote}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#050b1c] py-14">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <p className="text-center text-xs font-bold tracking-[0.14em] text-emerald-400">
            {audience.eyebrow}
          </p>
          <h2 className="mt-3 text-center text-3xl font-extrabold md:text-4xl">
            {audience.title}
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {(audience.items || []).map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-white/10 bg-[#0b1224] px-5 py-6"
              >
                <h3 className="text-lg font-extrabold text-[#2EE6A0]">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 md:px-8">
        <h2 className="mb-6 text-center text-3xl font-extrabold">
          {faqs.title}
        </h2>
        <div className="flex flex-col gap-3">
          {(faqs.items || []).map((item, index) => {
            const open = openFaq === index;
            return (
              <button
                key={item.q}
                type="button"
                aria-expanded={open}
                onClick={() => setOpenFaq(open ? null : index)}
                className="min-h-12 rounded-xl border border-white/10 bg-[#0b1224] px-5 py-4 text-left"
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm font-semibold">{item.q}</p>
                  <span className="text-lg font-light text-emerald-400" aria-hidden="true">
                    {open ? "−" : "+"}
                  </span>
                </div>
                {open ? (
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    {item.a}
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section id="contact" className="scroll-mt-24 border-t border-white/5 bg-[#050b1c] py-14">
        <div className="mx-auto grid max-w-6xl items-center gap-8 px-4 md:grid-cols-[1.1fr_0.9fr] md:px-8">
          <div>
            <p className="text-xs font-bold tracking-[0.14em] text-emerald-400">
              {contact.eyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-extrabold md:text-4xl">
              {contact.title}
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-400">
              {contact.text}
            </p>
            <Link
              to="/login"
              className="riceqr-login-btn-miller mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-slate-900"
            >
              <MdLogin size={18} aria-hidden="true" />
              {contact.loginButton}
            </Link>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#0b1224] p-6">
            <p className="flex items-start gap-3 text-sm text-slate-300">
              <FiMapPin className="mt-0.5 shrink-0 text-emerald-400" aria-hidden="true" />
              {contact.dealerLine1}
              <br />
              {contact.dealerLine2}
              <br />
              {contact.dealerLine3}
            </p>
            <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <FiPhone className="shrink-0 text-emerald-400" aria-hidden="true" />
              {phoneNumbers(contact).map((number, index) => (
                <Fragment key={`phone-${index}`}>
                  {index > 0 ? <span className="text-slate-600">·</span> : null}
                  <a
                    href={telHref(number)}
                    className="inline-flex min-h-11 items-center text-emerald-300 hover:text-emerald-200"
                  >
                    {number}
                  </a>
                </Fragment>
              ))}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {whatsappNumbers(contact).map((number, index) => (
                <Fragment key={`whatsapp-${index}`}>
                  {index > 0 ? <span className="text-slate-600">·</span> : null}
                  <a
                    href={whatsappHref(number)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center text-emerald-300 hover:text-emerald-200"
                  >
                    WhatsApp {number}
                  </a>
                </Fragment>
              ))}
            </p>
            <a
              href={`mailto:${contact.email}`}
              className="mt-4 inline-flex min-h-11 items-center text-sm text-slate-300 hover:text-white"
            >
              {contact.email}
            </a>
          </div>
        </div>
      </section>
      </main>

      <footer className="border-t border-white/5 px-4 py-6 text-center text-xs tracking-wide text-slate-300">
        <VisitorCounter className="mb-4" />
        {footer.copyright}
      </footer>
    </div>
  );
};

export default Home;
